import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { FaFileInvoice, FaSearch, FaUpload, FaCheckCircle, FaTimesCircle, FaFileExcel } from 'react-icons/fa'
import { FiX, FiUpload, FiFile } from 'react-icons/fi'
import { MdOutlineReceiptLong } from 'react-icons/md'
import { useAuth } from '../../../provider/auth.provider'
import api from '../../../libs/axios'
import { Pagination } from '../../../utils/pagination.utils'
import { useDebouncedCallback } from '../../../hooks/useDebounce'
import toast from 'react-hot-toast'
import { uploadFilesWithPresignedPayload } from '../../../libs/presigned-upload'
import { ExcelDownload } from '../../../utils/excel.utils'

interface POSite {
  id: number
  site_id: string
  site_name?: string
  reference_number?: string
  project?: { id: number; project_name: string; activity?: { activity_name: string } }
}

interface Invoice {
  id: number
  po_number: string
  po_date: string
  po_document?: string
  item: string
  item_description: string
  invoice_number?: string
  invoice_date?: string
  invoice_document?: string
  working_month?: string | null
  sales_month?: string | null
  po_received_status?: string | null
  status: string
  rejection_reason?: string | null
  vendor?: { id: number; vendor_full_name: string; vendor_code?: string }
  billing_coordinator?: { id: number; first_name: string; last_name: string }
  site?: {
    id: number
    site_id: string
    site_name?: string
    project?: { id: number; project_name: string; activity?: { id: number; activity_name: string } }
  }
  sites?: POSite[]
  site_count?: number
}

// Number of site IDs shown before the "View More" toggle kicks in.
const SITE_ID_PREVIEW = 3

// Column headers — identical to Billing "PO Issued" (item 3).
const PO_HEADERS = [
  'PO Number', 'PO Date', 'Vendor', 'Site Qty', 'Site ID', 'Site Name', 'Project', 'Activity',
  'Item Name', 'Item Description', 'Working Month', 'Sales Month', 'PO Received',
  'PO Doc', 'Invoice Number', 'Invoice Date', 'Vendor Invoice Stages', 'Coordinator',
]

// Resolve the list of sites a PO covers (falls back to the legacy single site).
const poSites = (rec: Invoice): POSite[] =>
  rec.sites && rec.sites.length ? rec.sites : (rec.site ? [rec.site as POSite] : [])

interface PaginationData {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next_page: boolean
  has_prev_page: boolean
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_generated: { label: 'PO Generated', color: 'bg-blue-100 text-blue-700' },
  invoice_submitted: { label: 'Invoice Submitted', color: 'bg-amber-100 text-amber-700' },
  invoice_booked: { label: 'Invoice Booked', color: 'bg-green-100 text-green-700' },
  invoice_rejected: { label: 'Invoice Rejected', color: 'bg-red-100 text-red-700' },
}

const ELIGIBLE_FOR_FILING = ['po_generated', 'invoice_rejected']

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
}

const formatDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// ── File Billing Offcanvas ────────────────────────────────────────────────────
interface FileBillingOffcanvasProps {
  isOpen: boolean
  onClose: () => void
  selectedIds: number[]
  onSuccess: () => void
  canFileBilling: boolean
}

const FileBillingOffcanvas: React.FC<FileBillingOffcanvasProps> = ({ isOpen, onClose, selectedIds, onSuccess, canFileBilling }) => {
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [docFile, setDocFile] = useState<File | null>(null)
  const [basicAmount, setBasicAmount] = useState('')
  const [gstAmount, setGstAmount] = useState('')
  const [totalAmount, setTotalAmount] = useState('')
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) { setInvoiceNumber(''); setInvoiceDate(''); setDocFile(null); setBasicAmount(''); setGstAmount(''); setTotalAmount('') }
  }, [isOpen])

  // Total Amount is auto-calculated from Basic + GST
  useEffect(() => {
    const basic = parseFloat(basicAmount)
    const gst = parseFloat(gstAmount)
    if (basicAmount === '' && gstAmount === '') { setTotalAmount(''); return }
    const total = (isNaN(basic) ? 0 : basic) + (isNaN(gst) ? 0 : gst)
    setTotalAmount(total ? String(Number(total.toFixed(2))) : '')
  }, [basicAmount, gstAmount])

  const handleSubmit = async () => {
    if (!invoiceNumber.trim() || !invoiceDate) {
      toast.error('Invoice number and date are required')
      return
    }
    if (!canFileBilling) {
      toast.error('You do not have permission to file billing')
      return
    }

    setSubmitting(true)
    try {
      let documentUrl: string | undefined

      if (docFile) {
        setUploading(true)
        try {
          const uploaded = await uploadFilesWithPresignedPayload([docFile], { folderName: 'vendor-invoices' })
          if (uploaded?.[0]?.fileUrl) documentUrl = uploaded[0].fileUrl
        } finally {
          setUploading(false)
        }
      }

      await api.post('/vendor-portal/invoices/file-billing', {
        invoice_ids: selectedIds,
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate,
        ...(documentUrl ? { invoice_document: documentUrl } : {}),
        ...(basicAmount ? { invoice_basic_amount: Number(basicAmount) } : {}),
        ...(gstAmount ? { invoice_gst_amount: Number(gstAmount) } : {}),
        ...(totalAmount ? { invoice_total_amount: Number(totalAmount) } : {}),
      })

      onSuccess()
      onClose()
    } catch {
      // toast handled by interceptor
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity z-40 ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FiUpload className="w-5 h-5" />File Billing
            </h2>
            <p className="text-blue-200 text-sm mt-0.5">{selectedIds.length} invoice{selectedIds.length !== 1 ? 's' : ''} selected</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-blue-200 p-1"><FiX className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-blue-50 border border-blue-100 rounded p-3 text-sm text-blue-700">
            The same invoice number and date will be applied to all selected invoices.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Invoice Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. INV-2024-001"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Invoice Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Basic Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="0.00"
                value={basicAmount}
                onChange={(e) => setBasicAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">GST Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="0.00"
                value={gstAmount}
                onChange={(e) => setGstAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Total Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                readOnly
                title="Auto-calculated from Basic + GST"
                className="w-full px-3 py-2.5 border border-gray-300 rounded bg-gray-100 text-gray-700 cursor-not-allowed text-sm"
                placeholder="0.00"
                value={totalAmount}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Invoice Document <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div
              className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              {docFile ? (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <FiFile className="w-4 h-4 text-blue-600" />
                  <span className="truncate max-w-[260px]">{docFile.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDocFile(null) }}
                    className="text-red-500 hover:text-red-700 ml-1"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  <FiUpload className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                  Click to upload PDF, JPG, or PNG
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => setDocFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
          <button type="button" onClick={onClose} disabled={submitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!invoiceNumber.trim() || !invoiceDate || submitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-700 rounded hover:bg-blue-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting || uploading
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />{uploading ? 'Uploading...' : 'Submitting...'}</>
              : <><FaCheckCircle className="w-4 h-4" />Submit Invoice</>}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────
const VendorPortalInvoices: React.FC = () => {
  const { hasPermission } = useAuth()
  const canFileBilling = hasPermission('VendorPortal', 'file_billing')

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total_count: 0, total_pages: 1, has_next_page: false, has_prev_page: false })
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [offcanvasOpen, setOffcanvasOpen] = useState(false)
  const [siteExportOpen, setSiteExportOpen] = useState(false)
  const [siteExportData, setSiteExportData] = useState<any[]>([])
  const [siteExportName, setSiteExportName] = useState('po_sites')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set())

  const toggleExpanded = (id: number) =>
    setExpandedSites((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  // Download the full site list covered by a single PO (item 3b).
  const handleDownloadSites = async (inv: Invoice) => {
    setDownloadingId(inv.id)
    try {
      const res = await api.get(`/vendor-portal/invoices/${inv.id}/sites`)
      const rows: any[] = res.data?.data?.rows ?? []
      if (!rows.length) { toast.error('No sites found for this PO'); return }
      // Per-PO site Excel — only the columns requested in item 6/7 (mirrors PO Issued).
      setSiteExportData(rows.map((s) => ({
        'Site ID': s.site_id ?? '—',
        'Site Name': s.site_name ?? '—',
        'Activity': s.activity?.activity_name ?? '—',
        'Project': s.project?.project_short_name ?? s.project?.project_name ?? '—',
        'Final Vendor Name': s.final_vendor_name ?? s.vendor?.vendor_full_name ?? '—',
        'PO Number': s.po_number ?? '—',
        'PO Date': formatDate(s.po_date),
        'Item Name': s.item ?? '—',
        'Item Description': s.item_description ?? '—',
      })))
      setSiteExportName(`po_${inv.po_number}_sites`)
      setSiteExportOpen(true)
    } catch {
      toast.error('Failed to load PO sites')
    } finally {
      setDownloadingId(null)
    }
  }

  const fetchInvoices = useCallback(async (overrides: { page?: number; limit?: number; search?: string; status?: string } = {}) => {
    setLoading(true)
    try {
      const params: any = {
        page: overrides.page ?? pagination.page,
        limit: overrides.limit ?? pagination.limit,
      }
      const s = overrides.search !== undefined ? overrides.search : search
      if (s) params.search = s
      const st = overrides.status !== undefined ? overrides.status : statusFilter
      if (st) params.status = st

      const res = await api.get('/vendor-portal/invoices', { params })
      if (res.data.success) {
        setInvoices(res.data.data.rows || [])
        setPagination(res.data.data.pagination || pagination)
        setSelected(new Set())
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit, search, statusFilter])

  useEffect(() => { fetchInvoices({ page: 1 }) }, [])

  const debouncedSearch = useDebouncedCallback((val: string) => fetchInvoices({ search: val, page: 1 }), 400)

  const eligibleForFiling = invoices.filter((i) => ELIGIBLE_FOR_FILING.includes(i.status))
  const selectedEligible = Array.from(selected).filter((id) => {
    const inv = invoices.find((i) => i.id === id)
    return inv && ELIGIBLE_FOR_FILING.includes(inv.status)
  })

  const allEligibleSelected = eligibleForFiling.length > 0 && eligibleForFiling.every((i) => selected.has(i.id))

  const toggleInvoice = (id: number) => {
    const inv = invoices.find((i) => i.id === id)
    if (!inv || !ELIGIBLE_FOR_FILING.includes(inv.status)) {
      toast.error('Only PO Generated or Rejected invoices can be filed for billing')
      return
    }
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  const handleSelectAll = () => {
    if (allEligibleSelected) setSelected(new Set())
    else setSelected(new Set(eligibleForFiling.map((i) => i.id)))
  }

  return (
    <>
      <Helmet><title>Vendor Portal – Invoices</title></Helmet>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <MdOutlineReceiptLong className="w-6 h-6 text-blue-700" />Purchase Orders
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">View your POs and file billing invoices</p>
            </div>
            {selectedEligible.length > 0 && canFileBilling && (
              <button
                type="button"
                onClick={() => setOffcanvasOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded hover:bg-blue-800 transition-colors shadow-sm"
              >
                <FiUpload className="w-4 h-4" />File Billing ({selectedEligible.length})
              </button>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/30 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 max-w-md relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by site ID or name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); debouncedSearch(e.target.value) }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); fetchInvoices({ status: e.target.value, page: 1 }) }}
            >
              <option value="">All Stages</option>
              <option value="po_generated">PO Generated</option>
              <option value="invoice_submitted">Invoice Submitted</option>
              <option value="invoice_booked">Invoice Booked</option>
              <option value="invoice_rejected">Invoice Rejected</option>
            </select>
          </div>
        </div>

        {selected.size > 0 && (
          <div className="px-4 sm:px-6 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
            <span className="text-sm text-blue-700 font-medium">{selected.size} invoice{selected.size !== 1 ? 's' : ''} selected for filing</span>
            <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-blue-600 hover:text-blue-800 underline">Clear</button>
          </div>
        )}

        {/* Table — columns mirror Billing "PO Issued" exactly (item 3), plus a
            leading filing-selection checkbox (vendor-portal only). */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {canFileBilling && (
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox"
                      checked={allEligibleSelected && eligibleForFiling.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title="Select all eligible for billing"
                    />
                  </th>
                )}
                {PO_HEADERS.map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={canFileBilling ? PO_HEADERS.length + 1 : PO_HEADERS.length} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                      <span className="text-sm text-gray-500">Loading invoices...</span>
                    </div>
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={canFileBilling ? PO_HEADERS.length + 1 : PO_HEADERS.length} className="py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaFileInvoice className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No PO found</p>
                      <p className="text-xs text-gray-400">Your billing coordinator will generate POs once work is accepted</p>
                    </div>
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const isEligible = ELIGIBLE_FOR_FILING.includes(inv.status)
                  const isChecked = selected.has(inv.id)
                  const sl = poSites(inv)
                  const ids = sl.map((s) => s.site_id)
                  const expanded = expandedSites.has(inv.id)
                  const shownIds = expanded ? ids : ids.slice(0, SITE_ID_PREVIEW)
                  return (
                    <tr key={inv.id} className={`hover:bg-gray-50 transition-colors ${isChecked ? 'bg-blue-50' : ''}`}>
                      {canFileBilling && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleInvoice(inv.id)}
                            disabled={!isEligible}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                            title={!isEligible ? 'Already filed or booked' : 'Select for filing'}
                          />
                        </td>
                      )}
                      <td className="px-3 py-3 font-mono font-semibold text-blue-700 whitespace-nowrap text-xs">{inv.po_number}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(inv.po_date)}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {inv.vendor ? (
                          <div>
                            <div className="font-medium">{inv.vendor.vendor_full_name}</div>
                            {inv.vendor.vendor_code && <div className="text-gray-400">{inv.vendor.vendor_code}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <span
                            className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold"
                            title={ids.join(', ')}
                          >
                            {inv.site_count ?? sl.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDownloadSites(inv)}
                            disabled={downloadingId === inv.id}
                            title="Download site list (Excel)"
                            className="text-green-600 hover:text-green-800 disabled:opacity-40"
                          >
                            {downloadingId === inv.id
                              ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b border-green-600" />
                              : <FaFileExcel className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs font-mono text-gray-700 max-w-[200px]">
                        {ids.length === 0 ? '—' : (
                          <div className="space-y-1">
                            <div className="whitespace-normal break-words">{shownIds.join(', ')}</div>
                            {ids.length > SITE_ID_PREVIEW && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(inv.id)}
                                className="text-blue-600 hover:text-blue-800 font-sans font-medium"
                              >
                                {expanded ? 'View Less' : `+${ids.length - shownIds.length} View More`}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-900 max-w-[200px]">
                        <span title={sl.map((s) => s.site_name).filter(Boolean).join(', ')} className="line-clamp-2">
                          {sl.map((s) => s.site_name).filter(Boolean).join(', ') || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{sl[0]?.project?.project_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-500">{sl[0]?.project?.activity?.activity_name || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 font-medium">{inv.item || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate" title={inv.item_description || ''}>{inv.item_description || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{inv.working_month || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700">{inv.sales_month || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 capitalize">{inv.po_received_status || '—'}</td>
                      <td className="px-3 py-3">
                        {inv.po_document ? (
                          <a href={inv.po_document} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs underline">View PO</a>
                        ) : <span className="text-gray-300 text-xs">-</span>}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-900">{inv.invoice_number || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(inv.invoice_date)}</td>
                      <td className="px-3 py-3"><StatusBadge status={inv.status} /></td>
                      <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {inv.billing_coordinator ? `${inv.billing_coordinator.first_name} ${inv.billing_coordinator.last_name}` : '—'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          pagination={pagination as any}
          onPageChange={(p) => fetchInvoices({ page: p })}
          onLimitChange={(l) => { setPagination((prev) => ({ ...prev, limit: l })); fetchInvoices({ page: 1, limit: l }) }}
        />
      </div>

      <FileBillingOffcanvas
        isOpen={offcanvasOpen}
        onClose={() => setOffcanvasOpen(false)}
        selectedIds={selectedEligible}
        onSuccess={() => fetchInvoices({ page: 1 })}
        canFileBilling={canFileBilling}
      />

      <ExcelDownload
        isOpen={siteExportOpen}
        onClose={() => setSiteExportOpen(false)}
        data={siteExportData}
        filename={siteExportName}
        title="Export PO Site List"
        description="Download the list of sites covered by this PO"
      />
    </>
  )
}

export default VendorPortalInvoices


